<template>
  <div class="attendee-limit">
    <v-text-field
      v-model="value"
      class="attendee-limit__field"
      :class="{ 'attendee-limit__field--reached': isReached, 'attendee-limit__field--empty': !value }"
      outline
      type="number"
      min="1"
      :label="value ? adminText('workflowsAll.attendee_limit_label', 'Attendee limit') : undefined"
      :placeholder="adminText('workflowsAll.insert_attendee_limit', 'Insert attendee limit')"
      :error="isReached"
      :error-messages="reachedMessage"
      :hide-details="!isReached"
      @keydown="blockNonDigits"
      @keyup.enter="submit"
    />
    <main-button
      v-if="isSaved"
      depressed
      color="error"
      :loading="loading"
      :text="adminText('workflowsAll.delete_d6f5636', 'DELETE')"
      @click="removeLimit"
    />
    <main-button
      v-else
      depressed
      :loading="loading"
      :text="adminText('workflowsAll.attendee_limit_confirm', 'Confirm')"
      @click="submit"
    />
    <simple-snackbar :open.sync="snackbar.open" :message-prop="snackbar.message" :color="snackbar.color" />
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import { SET_ATTENDEE_LIMIT } from '../../graphql/event.graphql'
import SimpleSnackbar from '../shared/snackbars/simple-snackbar.vue'

export default {
  mixins: [WorkflowDisplay],
  name: 'attendee-limit-field',
  components: { SimpleSnackbar },
  props: {
    eventId: String,
    attendeeLimit: Number,
    registeredCount: Number,
  },
  data() {
    return {
      value: this.attendeeLimit ? String(this.attendeeLimit) : '',
      loading: false,
      snackbar: { open: false, message: '', color: 'error' },
    }
  },
  computed: {
    parsedValue() {
      return Number(this.value)
    },
    isValid() {
      return Number.isInteger(this.parsedValue) && this.parsedValue >= 1
    },
    isSaved() {
      return !!this.attendeeLimit && this.parsedValue === this.attendeeLimit
    },
    isReached() {
      return !!this.attendeeLimit && this.registeredCount >= this.attendeeLimit
    },
    reachedMessage() {
      if (!this.isReached) return []

      return [
        this.adminText(
          'workflowsAll.registration_limit_reached',
          'Registration limit reached ({{count}}/{{limit}})',
          { count: this.registeredCount, limit: this.attendeeLimit },
        ),
      ]
    },
  },
  watch: {
    attendeeLimit(limit) {
      this.value = limit ? String(limit) : ''
    },
  },
  methods: {
    blockNonDigits(e) {
      if (['e', 'E', '+', '-', '.', ','].includes(e.key)) e.preventDefault()
    },
    submit() {
      if (!this.isValid || this.isSaved) return
      this.save(this.parsedValue)
    },
    removeLimit() {
      this.save(null)
    },
    async save(attendeeLimit) {
      this.loading = true
      try {
        const { errors } = await this.$apollo.mutate({
          mutation: SET_ATTENDEE_LIMIT,
          variables: { eventId: this.eventId, attendeeLimit },
        })
        if (errors) {
          this.snackbar = { open: true, message: errors[0].message, color: 'error' }

          return
        }
        this.$emit('updated')
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.attendee-limit {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.attendee-limit__field {
  flex: 0 0 264px;
  width: 264px;
  margin: 0;
  padding: 0;
}

.attendee-limit__field--reached {
  flex-basis: 336px;
  width: 336px;
}

.attendee-limit__field >>> .v-input__slot {
  min-height: 56px;
  margin-bottom: 0;
  border-radius: 4px;
  border: 1px solid #d9d9d9 !important;
}

.attendee-limit__field--reached >>> .v-input__slot {
  border-color: #db384c !important;
}

.attendee-limit__field >>> input {
  font-family: 'Roboto', sans-serif;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.5px;
  color: #333;
}

.attendee-limit__field--empty >>> input {
  margin-top: 0;
  align-self: center;
}

.attendee-limit__field >>> input::placeholder {
  color: #666;
}

.attendee-limit__field >>> .v-label {
  font-family: 'Roboto', sans-serif;
  font-size: 16px;
  color: #666;
}

.attendee-limit__field >>> .v-label--active {
  font-size: 12px;
  line-height: 16px;
}

.attendee-limit__field--reached >>> .v-label {
  color: #db384c !important;
}

.attendee-limit__field >>> .v-messages {
  font-family: 'Roboto', sans-serif;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #db384c;
}

.attendee-limit__field >>> input[type='number']::-webkit-inner-spin-button,
.attendee-limit__field >>> input[type='number']::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.attendee-limit .main-button {
  height: 48px;
  margin-top: 4px;
}
</style>
